/**
 * PrivateRoute.jsx - Scriptus Pharma Partner
 * Componente de rota protegida com verificação de sessão
 * 
 * ✅ Verificação de autenticação
 * ✅ Verificação de plano (Basic/Partner)
 * ✅ Verificação de limites de dispensação
 * ✅ Verificação periódica de sessão (30s)
 * ✅ Validação ao ganhar foco/visibilidade
 * ✅ Logout automático se sessão inválida
 */

import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../services/api';

// ============================================
// COMPONENTES DE UI
// ============================================

function LoadingScreen({ message = 'Carregando...' }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
        <p className="text-gray-600 sf-pro-text font-medium">{message}</p>
      </div>
    </div>
  );
}

function SessionExpiredScreen() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-4">
      <div className="glass-card-elevated rounded-2xl p-8 sm:p-12 shadow-lg border border-gray-200/50 max-w-lg mx-auto text-center">
        {/* Ícone */}
        <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m0 0v2m0-2h2m-2 0H10m5-6a5 5 0 11-10 0 5 5 0 0110 0z" />
          </svg>
        </div>
        
        {/* Título */}
        <h1 className="text-2xl sf-pro-display font-bold text-gray-900 mb-3">
          Sessão <span className="text-amber-600">Encerrada</span>
        </h1>
        
        {/* Descrição */}
        <p className="text-gray-600 sf-pro-text mb-6">
          Sua sessão foi encerrada automaticamente. Isso acontece quando você faz login 
          em outro dispositivo ou por motivos de segurança.
        </p>
        
        {/* Info */}
        <div className="bg-blue-50/50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-blue-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm sf-pro-text">
              Redirecionando para login...
            </span>
          </div>
        </div>
        
        {/* Botão */}
        <a 
          href="/login" 
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white rounded-xl font-semibold sf-pro-text transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
          </svg>
          Fazer Login Novamente
        </a>
      </div>
    </div>
  );
}

function UpgradeRequired() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-4">
      <div className="glass-card-elevated rounded-2xl p-8 sm:p-12 shadow-lg border border-gray-200/50 max-w-lg mx-auto text-center">
        {/* Ícone */}
        <div className="w-20 h-20 bg-gradient-to-br from-purple-100 to-purple-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
          </svg>
        </div>
        
        {/* Título */}
        <h1 className="text-2xl sf-pro-display font-bold text-gray-900 mb-3">
          Recurso exclusivo <span className="text-purple-600">Partner</span>
        </h1>
        
        {/* Descrição */}
        <p className="text-gray-600 sf-pro-text mb-6">
          Esta funcionalidade está disponível apenas para farmácias com plano Partner. 
          Faça upgrade para desbloquear todos os recursos.
        </p>
        
        {/* Benefícios */}
        <div className="bg-purple-50/50 rounded-xl p-4 mb-6 text-left">
          <p className="text-sm font-semibold text-purple-800 mb-3">Com o plano Partner você tem:</p>
          <ul className="space-y-2">
            {[
              'Dispensações ilimitadas',
              'Catálogo de produtos no marketplace',
              'Receba leads de pacientes',
              'Analytics avançado',
              'Suporte prioritário'
            ].map((benefit, i) => (
              <li key={i} className="flex items-center gap-2 text-sm text-purple-700">
                <svg className="w-4 h-4 text-purple-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {benefit}
              </li>
            ))}
          </ul>
        </div>
        
        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3">
          <a 
            href="/assinatura?upgrade=partner" 
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-semibold sf-pro-text transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
            </svg>
            Fazer Upgrade
          </a>
          <a 
            href="/dashboard" 
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold sf-pro-text transition-all duration-300"
          >
            Voltar ao Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

function LimiteAtingido() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-white to-emerald-50/30 p-4">
      <div className="glass-card-elevated rounded-2xl p-8 sm:p-12 shadow-lg border border-gray-200/50 max-w-lg mx-auto text-center">
        {/* Ícone */}
        <div className="w-20 h-20 bg-gradient-to-br from-amber-100 to-amber-200 rounded-full flex items-center justify-center mx-auto mb-6">
          <svg className="w-10 h-10 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
        </div>
        
        {/* Título */}
        <h1 className="text-2xl sf-pro-display font-bold text-gray-900 mb-3">
          Limite de dispensações <span className="text-amber-600">atingido</span>
        </h1>
        
        {/* Descrição */}
        <p className="text-gray-600 sf-pro-text mb-6">
          Você atingiu o limite de 50 dispensações gratuitas deste mês. 
          Faça upgrade para o plano Partner e tenha dispensações ilimitadas.
        </p>
        
        {/* Info de reset */}
        <div className="bg-blue-50/50 rounded-xl p-4 mb-6">
          <div className="flex items-center justify-center gap-2 text-blue-700">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-sm sf-pro-text">
              Seu limite será renovado no dia 1º do próximo mês
            </span>
          </div>
        </div>
        
        {/* Botões */}
        <div className="flex flex-col sm:flex-row gap-3">
          <a 
            href="/assinatura?upgrade=partner" 
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700 text-white rounded-xl font-semibold sf-pro-text transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5"
          >
            Upgrade para Ilimitado
          </a>
          <a 
            href="/dashboard" 
            className="flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl font-semibold sf-pro-text transition-all duration-300"
          >
            Voltar ao Dashboard
          </a>
        </div>
      </div>
    </div>
  );
}

// ============================================
// PRIVATE ROUTE - COMPONENTE PRINCIPAL
// ============================================

/**
 * PrivateRoute com verificação de plano, limites e sessão
 * 
 * @param {ReactNode} children - Componente filho
 * @param {boolean} requirePartner - Se true, requer plano Partner
 * @param {boolean} checkDispensationLimit - Se true, verifica limite de dispensações
 */
export default function PrivateRoute({ 
  children, 
  requirePartner = false,
  checkDispensationLimit = false 
}) {
  const { 
    user, 
    pharmacy,
    loading, 
    isPartner,
    tipoPlano,
    statusAssinatura,
    podeDispensar,
    signOut,
    getAuthToken
  } = useAuth();
  
  const location = useLocation();

  // ============================================
  // ESTADOS PARA VERIFICAÇÃO DE SESSÃO
  // ============================================
  const [sessionValid, setSessionValid] = useState(true);
  const [checkingSession, setCheckingSession] = useState(false);
  const checkIntervalRef = useRef(null);
  const lastCheckRef = useRef(null);

  // ============================================
  // VERIFICAÇÃO DE SESSÃO NO BACKEND
  // ============================================
  const checkSessionValidity = async () => {
    if (!user || checkingSession) return;

    try {
      setCheckingSession(true);

      // Obtém token atual
      const token = await getAuthToken();
      if (!token) {
        console.log("🚨 Token não disponível - logout automático");
        setSessionValid(false);
        signOut();
        return;
      }

      // Usa a rota de health-check do backend
      // Rota: /api/v1/auth/pharmacy/health-check
      const response = await api.get('/api/v1/auth/pharmacy/health-check', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.status === 200) {
        lastCheckRef.current = new Date();
        setSessionValid(true);
      }

    } catch (error) {
      console.error("Erro ao verificar sessão:", error);
      
      // Se erro de autorização (401/403), força logout
      if (error.response?.status === 401 || error.response?.status === 403) {
        console.log("🚨 Sessão invalidada detectada - logout automático");
        setSessionValid(false);
        signOut();
      }
    } finally {
      setCheckingSession(false);
    }
  };

  // ============================================
  // EFEITO: Verificação periódica (30 segundos)
  // ============================================
  useEffect(() => {
    if (!user) {
      // Limpa verificação se não há usuário
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
      return;
    }

    // Verifica imediatamente quando usuário está presente
    checkSessionValidity();

    // Configura verificação a cada 30 segundos
    checkIntervalRef.current = setInterval(() => {
      const now = new Date();
      
      // Só verifica se passou mais de 25 segundos da última verificação
      if (!lastCheckRef.current || (now - lastCheckRef.current) > 25000) {
        checkSessionValidity();
      }
    }, 30000);

    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
        checkIntervalRef.current = null;
      }
    };
  }, [user]);

  // ============================================
  // EFEITO: Verificação ao ganhar foco/visibilidade
  // ============================================
  useEffect(() => {
    const handleFocus = () => {
      if (user && !checkingSession) {
        setTimeout(() => {
          checkSessionValidity();
        }, 1000);
      }
    };

    const handleVisibilityChange = () => {
      if (!document.hidden && user && !checkingSession) {
        setTimeout(() => {
          checkSessionValidity();
        }, 1000);
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, checkingSession]);

  // ============================================
  // EFEITO: Cleanup no unmount
  // ============================================
  useEffect(() => {
    return () => {
      if (checkIntervalRef.current) {
        clearInterval(checkIntervalRef.current);
      }
    };
  }, []);

  // ============================================
  // LÓGICA DE RENDERIZAÇÃO
  // ============================================

  // 1. Loading
  if (loading) {
    return <LoadingScreen />;
  }
  
  // 2. Não autenticado → Login
  if (!user) {
    // Salva a URL atual para redirecionar após login
    const returnUrl = location.pathname + location.search;
    return <Navigate to={`/login?returnUrl=${encodeURIComponent(returnUrl)}`} replace />;
  }
  
  // 3. Sessão invalidada → Tela de sessão expirada
  if (!sessionValid) {
    return <SessionExpiredScreen />;
  }
  
  // 4. Não é farmácia (edge case - usuário autenticado mas sem dados de farmácia)
  if (!pharmacy) {
    console.warn('Usuário autenticado mas sem dados de farmácia');
    return <Navigate to="/login" replace />;
  }

  // 5. Requer Partner mas é Basic
  if (requirePartner && !isPartner) {
    return <UpgradeRequired />;
  }
  
  // 6. Verifica limite de dispensações (apenas para Basic em rotas de dispensação)
  if (checkDispensationLimit && !isPartner) {
    const podeDispensarAgora = podeDispensar ? podeDispensar() : true;
    
    if (!podeDispensarAgora) {
      return <LimiteAtingido />;
    }
  }

  // 7. Partner com assinatura inativa/cancelada (log apenas)
  if (tipoPlano === 'partner' && statusAssinatura !== 'ativa') {
    console.log('Partner com status:', statusAssinatura);
  }
  
  // ✅ Tudo OK - renderiza children
  return children;
}

// ============================================
// EXPORT ADICIONAL: PublicRoute
// ============================================

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  // Se já logado, redireciona para dashboard
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
}